import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Linking,
} from 'react-native';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import {
  FilterSelect,
  DEFAULT_ACCOUNT_BLOCKSIZE,
  type AccountFilterOption,
} from '../account/ManageAccountsSearchBar';
import {
  SkinTypeDefStorage,
  SKIN_TYPE_DEF_SCHEMA_VERSION,
  type SkinSettingDto,
  type SkinAppLogTypeDto,
} from '../../../storage/SkinTypeDefStorage';
import { ensureBundledSkinAssets } from '../../../services/skin/resolveSkinChannelInfo';
import { isOvershotEmptyPage } from '../../../services/clients/ClientSearchEngine';
import { usePersistedViewState } from '../../../hooks/usePersistedViewState';
import { AdminLogService } from '../../../services/AdminLogService';
import type { LogSearchRequestDto, LogSearchResultDto } from '../../../types/api';

interface LogsViewProps {
  apiService?: any;
  storage?: any;
}

interface LogSearchValues {
  accountId: string;
  sraId: string;
  logType: string;
  logSubType: string;
  level: string;
  startDateTime: string;
  endDateTime: string;
  blocksize: string;
}

interface LogPagingState {
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

// Messages longer than this are cut off in the table until the row is expanded.
const MESSAGE_PREVIEW_LENGTH = 200;

const LOG_BLOCKSIZE_OPTIONS = ['10', '20', '30', '40', '50', '60', '70', '80', '90', '100'] as const;

const EMPTY_VALUES: LogSearchValues = {
  accountId: '',
  sraId: '',
  logType: '',
  logSubType: '',
  level: '',
  startDateTime: '',
  endDateTime: '',
  blocksize: DEFAULT_ACCOUNT_BLOCKSIZE,
};

// Filter field -> value, in the order they are sent.
const FILTER_FIELDS: Array<[string, keyof LogSearchValues]> = [
  ['accountid', 'accountId'],
  ['sraid', 'sraId'],
  ['logtype', 'logType'],
  ['logsubtype', 'logSubType'],
  ['level', 'level'],
  ['startdatetime', 'startDateTime'],
  ['enddatetime', 'endDateTime'],
];

interface DateTimeFieldProps {
  value: string;
  label: string;
  onChange: (value: string) => void;
}

// Web uses the native datetime-local picker (value is 'YYYY-MM-DDTHH:mm'); native falls back to text.
const DateTimeField: React.FC<DateTimeFieldProps> = ({ value, label, onChange }) => {
  if (Platform.OS === 'web') {
    const webStyle = { ...webControlStyle, width: '100%', height: 36, borderRadius: 6, padding: '0 8px' } as any;
    return (
      <View style={styles.fill}>
        {React.createElement('input' as any, {
          type: 'datetime-local',
          value,
          onChange: (event: any) => onChange(String(event?.target?.value ?? '')),
          style: webStyle,
          'aria-label': label,
        })}
      </View>
    );
  }
  return (
    <TextInput
      style={styles.input}
      value={value}
      onChangeText={onChange}
      autoCapitalize="none"
      placeholder="YYYY-MM-DDTHH:mm"
      placeholderTextColor={EDITOR_COLORS.placeholder}
      accessibilityLabel={label}
    />
  );
};

export const LogsView: React.FC<LogsViewProps> = ({ apiService, storage }) => {
  const [values, setValues] = useState<LogSearchValues>(EMPTY_VALUES);
  // The values used for the last Search click; Prev/Next page with these, not with unsearched edits.
  const [searchedValues, setSearchedValues] = useState<LogSearchValues | null>(null);
  const [paging, setPaging] = useState<LogPagingState>({
    blocknum: '0',
    blocksize: DEFAULT_ACCOUNT_BLOCKSIZE,
    hasMore: false,
  });
  const [results, setResults] = useState<LogSearchResultDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isOpeningHangfire, setIsOpeningHangfire] = useState(false);
  const [hangfireError, setHangfireError] = useState<string | null>(null);
  const [logTypes, setLogTypes] = useState<SkinAppLogTypeDto[]>([]);
  const [logLevelOptions, setLogLevelOptions] = useState<AccountFilterOption[]>([]);

  // Keep filters and the current results page across tab switches and reloads.
  const persistedSnapshot = { values, searchedValues, paging, results, hasSearched };
  usePersistedViewState(
    storage,
    'adminView.logs',
    persistedSnapshot,
    (stored: Partial<typeof persistedSnapshot>) => {
      if (stored.values) setValues({ ...EMPTY_VALUES, ...stored.values });
      if (stored.searchedValues) setSearchedValues({ ...EMPTY_VALUES, ...stored.searchedValues });
      if (stored.paging) setPaging(stored.paging);
      if (stored.results) setResults(stored.results);
      if (stored.hasSearched) setHasSearched(true);
    }
  );

  // Log type and level lists come from the synced SkinTypeDefs, same as Manage Accounts.
  useEffect(() => {
    if (!storage) {
      return;
    }
    let cancelled = false;

    const toOptions = (lists: SkinSettingDto[][]): AccountFilterOption[] => {
      const byValue = new Map<string, AccountFilterOption>();
      lists.flat().forEach((entry) => {
        const value = (entry.value || '').trim();
        if (value && !byValue.has(value)) {
          byValue.set(value, { value, label: (entry.name || '').trim() || value });
        }
      });
      return Array.from(byValue.values());
    };

    void (async () => {
      try {
        const skinDefStorage = new SkinTypeDefStorage(storage);
        let defs = await skinDefStorage.getAllSkinTypeDefs();
        const isStale = defs.length === 0 || defs.some(def => (def.schemaVersion ?? 0) < SKIN_TYPE_DEF_SCHEMA_VERSION);
        if (isStale && Platform.OS === 'web') {
          try {
            await ensureBundledSkinAssets(storage);
            defs = await skinDefStorage.getAllSkinTypeDefs();
          } catch (bundleError) {
            console.warn('LogsView: failed to load bundled skin assets', bundleError);
          }
        }
        if (!cancelled) {
          // Merge log types across skins by TypeId, de-duplicating their subtypes by value.
          const byTypeId = new Map<string, SkinAppLogTypeDto>();
          defs.forEach((def) => {
            (def.appLogTypes ?? []).forEach((logType) => {
              const existing = byTypeId.get(logType.typeId);
              if (!existing) {
                byTypeId.set(logType.typeId, { ...logType, subTypes: [...(logType.subTypes ?? [])] });
              } else {
                (logType.subTypes ?? []).forEach((sub) => {
                  if (!existing.subTypes.some((s) => s.value === sub.value)) existing.subTypes.push(sub);
                });
              }
            });
          });
          setLogTypes(Array.from(byTypeId.values()));
          setLogLevelOptions(toOptions(defs.map(def => def.appLogLevels ?? [])));
        }
      } catch (loadError) {
        console.warn('LogsView: failed to load log type list', loadError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storage]);

  const setField = (field: keyof LogSearchValues, value: string) => {
    setValues(current => ({ ...current, [field]: value }));
  };

  const handleSearch = async (searchValues: LogSearchValues, blocknum: string = '0') => {
    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      if (!apiService) {
        throw new Error('API service not available');
      }

      const blocksize = searchValues.blocksize || DEFAULT_ACCOUNT_BLOCKSIZE;
      const request: LogSearchRequestDto = { blocknum, blocksize, searchTerms: [], searchFilters: [] };

      FILTER_FIELDS.forEach(([searchField, key]) => {
        const searchFilter = String(searchValues[key]).trim();
        if (searchFilter) {
          request.searchFilters.push({ searchField, searchFilter });
        }
      });

      const response = await new AdminLogService(apiService).searchLogs(request);
      const page = response.results ?? [];

      // Paged past the last block: keep the last good page and just disable Next.
      if (isOvershotEmptyPage(blocknum, page.length)) {
        setPaging(current => ({ ...current, hasMore: false }));
        return;
      }

      setResults(page);
      setExpandedRows(new Set());
      setSearchedValues(searchValues);
      setPaging({ blocknum, blocksize, hasMore: page.length >= parseInt(blocksize, 10) });
    } catch (err: any) {
      console.error('LogsView: Search failed', err);
      setError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown changes apply immediately and run a search with the updated values.
  const searchWith = (patch: Partial<LogSearchValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    handleSearch(next);
  };

  const toggleExpanded = (rowKey: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (!next.delete(rowKey)) next.add(rowKey);
      return next;
    });
  };

  const handleOpenHangfire = async () => {
    if (!apiService) {
      setHangfireError('API service not available');
      return;
    }
    setIsOpeningHangfire(true);
    setHangfireError(null);

    // Open the window synchronously inside the click so popup blockers allow it, then point it
    // at the dashboard once the backend grants access.
    const popup = Platform.OS === 'web' && typeof window !== 'undefined' ? window.open('', '_blank') : null;
    try {
      const { userStore } = resolveAuthContext();
      const { dashboardUrl } = await apiService.requestHangfireAccess({ userStore, storage });
      if (popup) {
        popup.location.href = dashboardUrl;
      } else if (Platform.OS === 'web') {
        window.open(dashboardUrl, '_blank');
      } else {
        await Linking.openURL(dashboardUrl);
      }
    } catch (err: any) {
      popup?.close();
      console.error('LogsView: Hangfire access failed', err);
      const status = err?.response?.status;
      setHangfireError(
        status === 403 ? 'You do not have access to the Hangfire console'
          : err?.message || 'Failed to open the Hangfire console'
      );
    } finally {
      setIsOpeningHangfire(false);
    }
  };

  const handleNextPage = () => {
    if (searchedValues) handleSearch(searchedValues, String(parseInt(paging.blocknum, 10) + 1));
  };

  const handlePrevPage = () => {
    if (searchedValues) handleSearch(searchedValues, String(Math.max(0, parseInt(paging.blocknum, 10) - 1)));
  };

  // The backend returns the log type value; show its AppLogTypes name when one matches.
  const logTypeOptions: AccountFilterOption[] = logTypes.map(t => ({ value: t.typeId, label: t.typeName || t.typeId }));
  const logTypeLabel = (value: string): string =>
    logTypeOptions.find(option => option.value === value)?.label ?? value;
  // SubType 0 is General — nothing to show.
  const logSubTypeLabel = (logType: string, subType: string): string => {
    if (!(parseInt(subType, 10) > 0)) return '';
    const sub = logTypes.find(t => t.typeId === logType)?.subTypes.find(s => s.value === subType);
    return sub?.name || subType;
  };

  // Sub types are prefiltered by the selected Log Type; with no type selected, offer none.
  const logSubTypeOptions: AccountFilterOption[] = (logTypes.find(t => t.typeId === values.logType)?.subTypes ?? [])
    .map(s => ({ value: s.value, label: s.name || s.value }));

  const logLevelLabel = (value: string): string =>
    logLevelOptions.find(option => option.value === value)?.label ?? value;

  const hasValues = FILTER_FIELDS.some(([, key]) => String(values[key]).trim().length > 0);
  const pageNum = parseInt(paging.blocknum, 10) || 0;
  const isPrevDisabled = isLoading || pageNum === 0 || !searchedValues;
  const isNextDisabled = isLoading || !paging.hasMore || !searchedValues;
  const displayValue = (LOG_BLOCKSIZE_OPTIONS as readonly string[]).includes(values.blocksize)
    ? values.blocksize
    : DEFAULT_ACCOUNT_BLOCKSIZE;
  const webSelectStyle = { ...webControlStyle, width: 64, borderRadius: 5, padding: '0 6px' } as any;

  const textFilter = (label: string, field: keyof LogSearchValues, placeholder: string) => (
    <View style={styles.group}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        style={styles.input}
        value={values[field]}
        onChangeText={v => setField(field, v)}
        autoCapitalize="none"
        returnKeyType="search"
        onSubmitEditing={() => handleSearch(values)}
        placeholder={placeholder}
        placeholderTextColor={EDITOR_COLORS.placeholder}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.searchBar}>
      <View style={styles.searchBarInner}>
        {/* Row 1: Search, Log Type, Log Sub Type, Display */}
        <View style={styles.row}>
          <TouchableOpacity
            onPress={() => handleSearch(values)}
            style={[styles.searchBtn, isLoading && styles.searchBtnDisabled]}
            disabled={isLoading}
            accessibilityLabel="Search logs"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.searchBtnText} numberOfLines={1}>🔍 Search</Text>
            )}
          </TouchableOpacity>
          <View style={styles.spacer} />
          <View style={styles.group}>
            <Text style={styles.label}>Log Type</Text>
            <FilterSelect
              value={values.logType}
              options={logTypeOptions}
              allLabel="All types"
              accessibilityLabel="Log type"
              onChange={v => searchWith({ logType: v, logSubType: '' })}
            />
          </View>
          <View style={styles.spacer} />
          <View style={styles.group}>
            <Text style={styles.label}>Log Sub Type</Text>
            <FilterSelect
              value={values.logSubType}
              options={logSubTypeOptions}
              allLabel="All sub types"
              accessibilityLabel="Log sub type"
              onChange={v => searchWith({ logSubType: v })}
            />
          </View>
          <View style={styles.spacer} />
          <View style={styles.displayRow}>
            <Text style={styles.displayLabel}>Display</Text>
            {Platform.OS === 'web' ? (
              React.createElement(
                'select' as any,
                {
                  value: displayValue,
                  onChange: (event: any) => searchWith({ blocksize: String(event?.target?.value ?? DEFAULT_ACCOUNT_BLOCKSIZE) }),
                  style: webSelectStyle,
                  'aria-label': 'Set display block size',
                },
                LOG_BLOCKSIZE_OPTIONS.map((option) => (
                  React.createElement('option' as any, { key: option, value: option }, option)
                ))
              )
            ) : (
              <TouchableOpacity
                onPress={() => {
                  const options = LOG_BLOCKSIZE_OPTIONS as readonly string[];
                  searchWith({ blocksize: options[(options.indexOf(displayValue) + 1) % options.length] });
                }}
                style={styles.displayBtn}
                accessibilityRole="button"
                accessibilityLabel="Set display block size"
              >
                <Text style={styles.displayBtnText}>{displayValue}</Text>
              </TouchableOpacity>
            )}
          </View>
          {hasValues && (
            <TouchableOpacity
              onPress={() => setValues(current => ({ ...EMPTY_VALUES, blocksize: current.blocksize }))}
              style={styles.clearBtn}
              accessibilityLabel="Clear search"
            >
              <Text style={styles.clearBtnText}>Clear</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Row 2: Level, Account Id, Sra Id */}
        <View style={[styles.row, styles.nextRow]}>
          <TouchableOpacity
            onPress={handleOpenHangfire}
            style={[styles.hangfireBtn, isOpeningHangfire && styles.searchBtnDisabled]}
            disabled={isOpeningHangfire}
            accessibilityLabel="Open Hangfire console"
          >
            {isOpeningHangfire ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.textSecondary} />
            ) : (
              <Text style={styles.hangfireBtnText} numberOfLines={1}>Hangfire Console</Text>
            )}
          </TouchableOpacity>
          <View style={styles.spacer} />
          <View style={styles.group}>
            <Text style={styles.label}>Level</Text>
            <FilterSelect
              value={values.level}
              options={logLevelOptions}
              allLabel="All levels"
              accessibilityLabel="Log level"
              onChange={v => searchWith({ level: v })}
            />
          </View>
          <View style={styles.spacer} />
          {textFilter('Account Id', 'accountId', 'Account ID')}
          <View style={styles.spacer} />
          {textFilter('Sra Id', 'sraId', 'SRA ID')}
        </View>

        {/* Row 3: Start / End datetime */}
        <View style={[styles.row, styles.nextRow]}>
          <View style={styles.searchSpacer} />
          <View style={styles.spacer} />
          <View style={styles.group}>
            <Text style={styles.label}>Start</Text>
            <DateTimeField value={values.startDateTime} label="Start date and time" onChange={v => setField('startDateTime', v)} />
          </View>
          <View style={styles.spacer} />
          <View style={styles.group}>
            <Text style={styles.label}>End</Text>
            <DateTimeField value={values.endDateTime} label="End date and time" onChange={v => setField('endDateTime', v)} />
          </View>
          {/* Empty third column keeps the date pickers the same width as the filters above. */}
          <View style={styles.spacer} />
          <View style={styles.group} />
        </View>

        {hangfireError ? <Text style={styles.hangfireErrorText}>{hangfireError}</Text> : null}
      </View>
      </View>

      <ScrollView style={styles.resultsContainer}>
        <View style={styles.resultsInner}>
          {error ? (
            <View style={styles.messageContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : !hasSearched ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>Enter search criteria and click Search to find logs</Text>
            </View>
          ) : isLoading ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>Searching...</Text>
            </View>
          ) : results.length === 0 ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>No logs found</Text>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <View style={[styles.tableCell, styles.logIdCol]}><Text style={styles.headerText}>Log Id</Text></View>
                <View style={[styles.tableCell, styles.dateCol]}><Text style={styles.headerText}>Date/Time</Text></View>
                <View style={[styles.tableCell, styles.accountCol]}><Text style={styles.headerText}>Account</Text></View>
                <View style={[styles.tableCell, styles.userCol]}><Text style={styles.headerText}>User</Text></View>
                <View style={[styles.tableCell, styles.messageCol]}><Text style={styles.headerText}>Message</Text></View>
                <View style={[styles.tableCell, styles.levelCol]}><Text style={styles.headerText}>Level</Text></View>
                <View style={[styles.tableCell, styles.typeCol]}><Text style={styles.headerText}>Type</Text></View>
                <View style={[styles.tableCell, styles.subTypeCol]}><Text style={styles.headerText}>Subtype</Text></View>
              </View>

              {results.map((result, index) => (
                <View
                  key={result.LogId || String(index)}
                  style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}
                >
                  <View style={[styles.tableCell, styles.logIdCol]}><Text style={styles.cellText}>{result.LogId}</Text></View>
                  <View style={[styles.tableCell, styles.dateCol]}><Text style={styles.cellText}>{result.DateTime}</Text></View>
                  <View style={[styles.tableCell, styles.accountCol]}><Text style={styles.cellText}>{result.AccountId}</Text></View>
                  <View style={[styles.tableCell, styles.userCol]}><Text style={styles.cellText}>{result.UserId}</Text></View>
                  <View style={[styles.tableCell, styles.messageCol]}>
                    {(() => {
                      const message = result.Message ?? '';
                      if (message.length <= MESSAGE_PREVIEW_LENGTH) {
                        return <Text style={styles.cellText}>{message}</Text>;
                      }
                      const rowKey = result.LogId || String(index);
                      const isExpanded = expandedRows.has(rowKey);
                      return (
                        <>
                          <Text style={styles.cellText}>
                            {isExpanded ? message : `${message.slice(0, MESSAGE_PREVIEW_LENGTH)}…`}
                          </Text>
                          <TouchableOpacity
                            onPress={() => toggleExpanded(rowKey)}
                            style={styles.expandBtn}
                            accessibilityRole="button"
                            accessibilityLabel={isExpanded ? 'Collapse message' : 'Expand message'}
                          >
                            <Text style={styles.expandBtnText}>{isExpanded ? '▲' : '▼'}</Text>
                          </TouchableOpacity>
                        </>
                      );
                    })()}
                  </View>
                  <View style={[styles.tableCell, styles.levelCol]}><Text style={styles.cellText}>{logLevelLabel(result.Level)}</Text></View>
                  <View style={[styles.tableCell, styles.typeCol]}><Text style={styles.cellText}>{logTypeLabel(result.LogType)}</Text></View>
                  <View style={[styles.tableCell, styles.subTypeCol]}><Text style={styles.cellText}>{logSubTypeLabel(result.LogType, result.LogSubType)}</Text></View>
                </View>
              ))}

              <View style={styles.pagination}>
                <TouchableOpacity
                  onPress={handlePrevPage}
                  disabled={isPrevDisabled}
                  style={[styles.pageBtn, isPrevDisabled && styles.pageBtnDisabled]}
                  accessibilityLabel="Previous page"
                >
                  <Text style={[styles.pageBtnText, isPrevDisabled && styles.pageBtnTextDisabled]}>← Prev</Text>
                </TouchableOpacity>
                <Text style={styles.pageNum}>Page {pageNum + 1}</Text>
                <TouchableOpacity
                  onPress={handleNextPage}
                  disabled={isNextDisabled}
                  style={[styles.pageBtn, isNextDisabled && styles.pageBtnDisabled]}
                  accessibilityLabel="Next page"
                >
                  <Text style={[styles.pageBtnText, isNextDisabled && styles.pageBtnTextDisabled]}>Next →</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
  },
  searchBar: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: EDITOR_COLORS.background,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  searchBarInner: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  nextRow: {
    marginTop: 10,
  },
  spacer: {
    width: 12,
  },
  searchSpacer: {
    width: 130,
  },
  fill: {
    flex: 1,
    minWidth: 0,
  },
  group: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    width: 105,
    textAlign: 'right',
    paddingRight: 8,
  },
  input: {
    ...editorText,
    flex: 1,
    minWidth: 0,
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    color: EDITOR_COLORS.textPrimary,
  },
  searchBtn: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    width: 130,
  },
  hangfireBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    width: 130,
  },
  hangfireBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  hangfireErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginTop: 8,
  },
  searchBtnDisabled: {
    opacity: 0.6,
  },
  searchBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  clearBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 8,
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  displayRow: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  displayLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  displayBtn: {
    width: 64,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  displayBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  resultsContainer: {
    flex: 1,
  },
  resultsInner: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  messageContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  messageText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    textAlign: 'center',
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableRowEven: {
    backgroundColor: EDITOR_COLORS.background,
  },
  tableRowOdd: {
    backgroundColor: '#f9fafb',
  },
  tableCell: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  // Fixed-width columns; Message takes all remaining width.
  logIdCol: {
    width: 80,
  },
  dateCol: {
    width: 180,
  },
  levelCol: {
    width: 100,
  },
  subTypeCol: {
    width: 120,
  },
  typeCol: {
    width: 140,
  },
  accountCol: {
    width: 100,
  },
  userCol: {
    width: 55,
  },
  messageCol: {
    flex: 1,
    minWidth: 240,
  },
  expandBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  expandBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  cellText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
  },
  pageBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
  },
  pageBtnDisabled: {
    opacity: 0.4,
  },
  pageBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  pageBtnTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  pageNum: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});



